import { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '../api/admin';
import { AuthCard } from './AuthLayout';

/**
 * "I cannot get in."
 *
 * The success state deliberately does not say whether the address is a login
 * here — that answer is only safe to give to whoever holds the inbox, and by
 * the time they read the email they already have it. So the page confirms what
 * it did (sent, if there was anywhere to send to) rather than what it found.
 *
 * The one thing it does report plainly is a server with no outgoing mail: that
 * is a fact about this install, not about the person, and leaving someone
 * waiting for an email that cannot be sent is the worse failure.
 *
 * R13 (ABC5-07): one small card on the canvas.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch (err: any) {
      setError(err.message || 'Could not send the email');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthCard>
        <h1 className="auth-title">Check your email</h1>
        <p className="auth-lede">
          If <b>{email.trim()}</b> is a Form-Magic login, a link to set a new password is on its
          way. It works once and expires in an hour.
        </p>
        <div className="auth-note ok">Your current password still works until you use the link.</div>
        <p className="auth-lede">
          Nothing arrived? Check the spam folder, then{' '}
          <button type="button" className="auth-link" onClick={() => setSent(false)}>try another address</button>.
          If your company signs in with SSO, there is no password to reset — use the
          single sign-on button on the sign-in page.
        </p>
        <Link className="fs-btn ink lg block" to="/login">Back to sign in</Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <form className="auth-stack" onSubmit={onSubmit}>
        <h1 className="auth-title">Reset your password</h1>
        <p className="auth-lede">Enter the email you sign in with and we&apos;ll send you a link to set a new password.</p>
        {error && <div className="auth-err" role="alert">{error}</div>}
        <div className="auth-field">
          <label htmlFor="forgot-email">Email</label>
          <input id="forgot-email" type="text" inputMode="email" autoFocus autoComplete="username" value={email}
            onChange={(e) => setEmail(e.target.value)} />
        </div>
        <button className="fs-btn primary lg block" type="submit" disabled={busy}>
          {busy ? 'Sending…' : 'Send the link'}
        </button>
        <div className="auth-links center">
          <Link to="/login">Back to sign in</Link>
        </div>
      </form>
    </AuthCard>
  );
}
